import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  ViewChild
} from '@angular/core'
import { Observable, BehaviorSubject, Subject, fromEvent } from 'rxjs'
import { map, take, takeUntil } from 'rxjs/operators'
import { AlertsService } from '../../providers/alerts/alerts.service'
import {
  GET_HISTORY_NOTIFICATION_LIST,
  READ_ALL_NOTIFICATIONS,
  READ_NOTIFICATION
} from './alerts.graphql'
import { DataService } from '../../data/providers/data.service'
import { QueryResult } from '../../data/query-result'
import { Router } from '@angular/router'
import { NotificationSeverity } from '../../shared/components/notification-icon/notification-severity.enum'

export enum NotifyType {
  BOOKING = 'BOOKING',
  RECONCILIATION_PERIOD = 'RECONCILIATION_PERIOD',
  OTHER = 'OTHER'
}

export interface NotificationItem {
  id: string
  createdAt: string
  updatedAt: string
  title: string
  body: string
  notificationBy: string
  type: string
  isRead: boolean
  userId: string
  description: string
  data: any
  notificationCategory?: string
}

interface HistoryNotification {
  id: string
  createdAt: string
  updatedAt: string
  title: string
  body: string
  notificationBy: string
  type: string
  isRead: boolean
  userId: string
  description: string
  data: any
  notificationCategory?: string
}

interface HistoryNotificationsResponse {
  adminEscape_HitoryNotifications: {
    totalItems: number
    totalBooking: number
    totalReconciliation: number
    totalOther: number
    totalUnread: number
    items: HistoryNotification[]
  }
}

interface ReadAllNotificationResponse {
  readAllNotification: {
    message: string
    status: boolean
    errorCode?: string
  }
}

interface ReadNotificationResponse {
  readSingleNotification: {
    message: string
    status: boolean
    errorCode?: string
  }
}

export enum NotificationTypeEntity {
  BOOKING = 'BOOKING',
  NORMAL = 'NORMAL',
  OTHER = 'OTHER',
  RATING = 'RATING',
  SCHEDULER = 'SCHEDULER',
  BOOKING_HOTEL = 'BOOKING_HOTEL',
  RECONCILIATION_PERIOD = 'RECONCILIATION_PERIOD',
  RECONCILIATION_DETAIL = 'RECONCILIATION_DETAIL',
  RECONCILIATION_PERIOD_LIST = 'RECONCILIATION_PERIOD_LIST'
}

@Component({
  selector: 'vdr-alerts',
  templateUrl: './alerts.component.html',
  styleUrls: ['./alerts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AlertsComponent implements OnDestroy {
  @ViewChild('dropdown') dropdown
  private notificationsSubject = new BehaviorSubject<HistoryNotification[]>([])
  private destroy$ = new Subject<void>()
  protected readingNotificationIds = new Set<string>()
  private historyNotificationsQuery: QueryResult<HistoryNotificationsResponse>
  protected NotificationTypeEntity = NotificationTypeEntity
  protected NotifyType = NotifyType
  protected NotificationSeverity = NotificationSeverity

  private selectedFilterTypeSubject = new BehaviorSubject<NotifyType>(
    NotifyType.BOOKING
  )
  protected selectedFilterType$ = this.selectedFilterTypeSubject.asObservable()
  protected selectedFilterType = NotifyType.BOOKING

  protected historyNotifications$: Observable<HistoryNotification[]>
  filteredNotifications$: Observable<HistoryNotification[]>
  private loadingSubject = new BehaviorSubject<boolean>(true)
  protected loading$ = this.loadingSubject.asObservable()

  protected unreadCount$: Observable<number>

  private totalUnreadSubject = new BehaviorSubject<number>(0)
  protected totalUnread$ = this.totalUnreadSubject.asObservable()

  constructor(
    protected alertsService: AlertsService,
    private dataService: DataService,
    private router: Router
  ) {
    this.initializeNotificationsQuery()
    this.listenForRealtimeNotifications()
  }

  private initializeNotificationsQuery() {
    this.historyNotificationsQuery = this.dataService
      .query<HistoryNotificationsResponse>(GET_HISTORY_NOTIFICATION_LIST, {
        options: {
          take: 99,
          type: this.selectedFilterType
        }
      })
      .refetchOnChannelChange()

    this.historyNotificationsQuery.stream$
      .pipe(map((result) => result.adminEscape_HitoryNotifications.items || []))
      .subscribe((items) => this.notificationsSubject.next(items))

    this.historyNotifications$ = this.notificationsSubject.asObservable()

    this.historyNotificationsQuery.stream$
      .pipe(
        map((result) => result.adminEscape_HitoryNotifications.totalUnread || 0)
      )
      .subscribe((value) => {
        this.totalUnreadSubject.next(value)
      })

    this.historyNotificationsQuery.stream$.pipe(take(1)).subscribe(() => {
      this.loadingSubject.next(false)
    })

    this.filteredNotifications$ = this.historyNotifications$

    this.unreadCount$ = this.historyNotifications$.pipe(
      map((notifications) => notifications.filter((n) => !n.isRead).length)
    )
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private listenForRealtimeNotifications(): void {
    fromEvent(window, 'merchant-notification-received')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshNotifications(true)
      })
  }

  protected updateSelectedFilterType(filterType: NotifyType) {
    this.selectedFilterType = filterType
    this.selectedFilterTypeSubject.next(filterType)
    this.refreshNotifications()
  }

  protected getFilterTypeDisplayName(filterType: NotifyType): string {
    switch (filterType) {
      case NotifyType.BOOKING:
        return 'Đặt phòng'
      case NotifyType.RECONCILIATION_PERIOD:
        return 'Đối soát và thanh toán'
      case NotifyType.OTHER:
        return 'Thông báo khác'
      default:
        return 'Khác'
    }
  }

  protected getNotificationSeverity(
    notification: HistoryNotification
  ): NotificationSeverity {
    switch (notification.type) {
      case NotifyType.BOOKING:
        if (
          notification.body?.toLowerCase().includes('hủy') ||
          notification.title?.toLowerCase().includes('hủy')
        ) {
          return NotificationSeverity.DANGER
        }
        return NotificationSeverity.SUCCESS
      case NotifyType.RECONCILIATION_PERIOD:
        if (
          notification.body?.toLowerCase().includes('công nợ') ||
          notification.title?.toLowerCase().includes('công nợ')
        ) {
          return NotificationSeverity.WARNING
        }
        return NotificationSeverity.SUCCESS
      case NotifyType.OTHER:
        return NotificationSeverity.INFO
      default:
        return NotificationSeverity.INFO
    }
  }

  protected formatTimestamp(timestamp: string): string {
    if (!timestamp) return ''

    try {
      const date = new Date(timestamp)
      return date.toLocaleString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    } catch (e) {
      return timestamp
    }
  }

  protected handleNotificationClick(notification: HistoryNotification) {
    this.readNotification(notification)

    switch (notification.type) {
      case NotifyType.BOOKING:
        this.handleBookingNotification(notification)
        break
      case NotifyType.RECONCILIATION_PERIOD:
        this.handleReconciliationNotification(notification)
        break
      default:
        this.router.navigate(['/notification', notification.id])
        this.dropdown.toggleOpen()
        break
    }
  }

  private handleBookingNotification(notification: HistoryNotification) {
    try {
      if (notification.data) {
        const data = JSON.parse(notification.data)
        if (data.venueId && data.bookingId) {
          this.router.navigate(['/orders', data.venueId, data.bookingId])
        } else if (data.bookingCode) {
          this.router.navigate(['/orders'], {
            queryParams: { bookingCode: data.bookingCode }
          })
        }
      }
    } catch (e) {
      console.error('Error parsing booking notification data:', e)
    }
  }

  private handleReconciliationNotification(notification: HistoryNotification) {
    try {
      if (notification.data) {
        const data = JSON.parse(notification.data)
        if (data.venueId && data.bookingId) {
          this.router.navigate(['/revenue'], {
            queryParams: { bookingCode: data.bookingCode || data.bookingId }
          })
        } else if (data.bookingCode) {
          this.router.navigate(['/revenue'], {
            queryParams: { bookingCode: data.bookingCode }
          })
        }
      }
    } catch (e) {
      console.error('Error parsing reconciliation notification data:', e)
    }
  }

  protected async readAllNotifications() {
    try {
      const result = await this.dataService
        .mutate<ReadAllNotificationResponse>(READ_ALL_NOTIFICATIONS)
        .toPromise()

      if (result?.readAllNotification?.status) {
        const currentItems = this.notificationsSubject.value
        this.notificationsSubject.next(
          currentItems.map((item) => ({ ...item, isRead: true }))
        )

        this.totalUnreadSubject.next(0)

        this.refreshNotifications(false)
      }
    } catch (e) {
      console.error('Error reading all notifications:', e)
    }
  }

  protected async readNotification(notification: HistoryNotification) {
    switch (notification.type) {
      case NotificationTypeEntity.BOOKING:
        const data = JSON.parse(notification.data)
        if (data.venueId && data.bookingId) {
          this.router.navigate(['/orders', data.venueId, data.bookingId])
        }
        break
      case NotificationTypeEntity.RATING:
        const dataRating = JSON.parse(notification.data)
        if (dataRating.venueId && dataRating.roomId) {
          this.router.navigate(['/review', dataRating.venueId], {
            queryParams: {
              roomId: dataRating.roomId
            }
          })
        }
        break
      default:
        break
    }

    if (
      this.readingNotificationIds.has(notification.id) ||
      notification.isRead
    ) {
      return
    }

    try {
      this.readingNotificationIds.add(notification.id)
      const result = await this.dataService
        .mutate<ReadNotificationResponse>(READ_NOTIFICATION, {
          notificationId: notification.id
        })
        .toPromise()

      if (result?.readSingleNotification?.status) {
        const currentItems = this.notificationsSubject.value
        this.notificationsSubject.next(
          currentItems.map((item) =>
            item.id === notification.id ? { ...item, isRead: true } : item
          )
        )

        this.totalUnreadSubject.next(Math.max(0, this.totalUnreadSubject.value - 1))
      }
    } catch (e) {
      console.error('Error reading notification:', e)
    } finally {
      this.readingNotificationIds.delete(notification.id)
    }
  }

  private refreshNotifications(showLoading = true): void {
    if (showLoading) {
      this.loadingSubject.next(true)
    }

    this.historyNotificationsQuery.ref
      .refetch({
        options: {
          take: 99,
          type: this.selectedFilterType
        }
      })
      .then(() => {
        this.historyNotificationsQuery.stream$
          .pipe(take(1))
          .subscribe((result) => {
            const response = result.adminEscape_HitoryNotifications
            this.notificationsSubject.next(response.items || [])
            this.totalUnreadSubject.next(response.totalUnread || 0)
            this.loadingSubject.next(false)
          })
      })
      .catch((error) => {
        console.error('Error refetching notifications:', error)
        this.loadingSubject.next(false)
      })
  }
}
